import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { Box, Flex, Text, Button, Image } from '@everlywell/ui-kit';
import { Link } from 'gatsby';
import React from 'react';
import { IVideoHeroProps } from 'utils/types';

const VideoHero: React.FC<IVideoHeroProps> = ({
  cta,
  richHeading,
  text,
  video,
  secondaryImage,
}) => {
  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: any, children: any) => (
        <Text display="block" mb="1rem">
          {children}
        </Text>
      ),
    },
    renderMark: {
      [MARKS.BOLD]: (text: any) => (
        <strong style={{ color: '#68ca9c' }}>{text}</strong>
      ),
      [MARKS.UNDERLINE]: (text: any) => <span>{text}</span>,
    },
  };

  return (
    <Box as="section" className="video-hero-wrapper">
      <Box maxWidth="1440px" mx="auto" pl="1rem" pr={['1rem', '0']}>
        <Flex
          direction={['column', 'column', 'row']}
          align="center"
          justify="space-around"
          height={['auto', '750px']}
          ml="auto"
          mt={['6.5rem', '6.5rem', '0']}
        >
          {/* Left column: Text content */}
          <Box
            width="100%"
            mb={['0', '4rem', '0']}
            pl={['0', '0', '1rem']}
            textAlign={['center', 'center', 'left']}
          >
            <Text
              as="div"
              fontSize={['40px', '56px', '56px']}
              fontWeight="400"
              lineHeight="1.1"
              data-testid="video-hero-heading"
            >
              {documentToReactComponents(JSON.parse(richHeading.raw), options)}
            </Text>
            <Text
              size="lg"
              color="#4b5563"
              my="1.25rem"
              lineHeight="1.5"
              maxW={['350px', '400px']}
              mx={['auto', 'auto', '0']}
            >
              {text}
            </Text>

            {cta && (
              <Link to={cta.url}>
                <Button
                  height="52px"
                  width={['100%', 'auto']}
                  px="3rem"
                  mt="0.75rem"
                  bg="#012b3b"
                  borderRadius="28px"
                  color="white"
                  fontSize="16px"
                  fontWeight="500"
                  _hover={{ opacity: 0.9 }}
                >
                  {cta.text}
                </Button>
              </Link>
            )}

            {secondaryImage && (
              <Image
                src={secondaryImage.file.url}
                alt="Secondary image"
                width="125px"
                height="auto"
                objectFit="cover"
                mt="1.8rem"
                mx={['auto', 'auto', '0']}
                display="block"
              />
            )}
          </Box>

          {/* Right column: Video with slanted edge */}
          <Box
            width="100%"
            height={['auto', '100%']}
            mt={['30px', '0']}
            position="relative"
          >
            <Box
              position={['relative', 'absolute']}
              inset="0"
              bg="white"
              clipPath={[
                'none',
                'none',
                'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)',
              ]}
            />
            <Box
              as="video"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-label="Healthcare professional demonstrating at-home lab testing"
              position={['relative', 'absolute']}
              borderRadius={['20px', '0']}
              inset="0"
              width="100%"
              height="100%"
              objectFit="cover"
              clipPath={[
                'none',
                'none',
                'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)',
              ]}
              data-testid="hero-video"
            >
              <source src={video.url} type="video/mp4" />
              Your browser does not support the video tag.
            </Box>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default VideoHero;
